import { gsap } from 'gsap';
import { settings } from '@/config';
import { createLogger } from '@/utils/logger';
import { StoryNarrator } from './StoryNarrator';
import { StoryAmbience } from '@/audio/StoryAmbience';
import type { StoryGrade } from '@/materials/storyGrade';

export interface StoryModeDeps {
  canvas: HTMLCanvasElement;
  /** The shared Perlin shadow-flow grade patched into every scene material. */
  grade: StoryGrade;
  /** Blend the sky + cloud sea toward the story's night palette (0 = authored, 1 = full). */
  setAtmosphere: (k: number) => void;
  /** Story opened / closed — the Experience locks scroll and ducks audio off these. */
  onOpen?: (chapterIndex: number) => void;
  onClose?: () => void;
  /** A USER-initiated close (the CLOSE button or Escape) — the Experience plays the close SFX
   *  off this (2026-09-01, user: "same sound effect on close" as KNOW MORE has). Deliberately
   *  not fired by the auto-return or a forceClose, which are not a press. */
  onCloseSound?: () => void;
  /** Raised while the narrator speaks, so the music bed can duck under her. */
  onSpeakingChange?: (speaking: boolean) => void;
}

/**
 * STORY MODE — the per-chapter "Know More" experience (2026-08-18).
 *
 * Owns the whole sequence: the KNOW MORE click, the black cloud-smoke grade rolling in on a
 * magnetic ease, and the narrated + subtitled story. See `settings.storyMode` for the flow in
 * the user's own words and {@link StoryNarrator} for the voice/subtitle sync.
 *
 * THE GRADE IS ONE UNIFORM, NOT A MUTATION. Every scene material is patched once at load with
 * the Perlin shadow-flow shader ({@link StoryGrade}); opening a story only animates `amount`
 * from 0 to 1. At 0 the shader is a no-op, so "everything comes back to the original material"
 * is true by construction — there is nothing to restore, because nothing was ever overwritten.
 * (v1 lerped and restored each `material.color`; that was both flat-looking and fragile, since
 * anything else touching those colours mid-story would have been clobbered on close.)
 */
/** Promise-based delay — keeps {@link StoryMode.enter} readable as a sequence. */
const wait = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));

export class StoryMode {
  private readonly log = createLogger('story');
  private readonly narrator = new StoryNarrator();
  private readonly ambience = new StoryAmbience();
  private readonly grade: HTMLDivElement;
  private readonly closeBtn: HTMLButtonElement;

  private open = false;
  private busy = false;
  /** Chapter currently under the pointer, whether or not it has a story. */
  private currentChapter = -1;

  private gradeTween: gsap.core.Tween | null = null;
  /** Mirrors the grade uniform so the atmosphere blend can follow the same curve. */
  private gradeAmount = { value: 0 };

  /** Bumped by every exit. `enter` re-checks it after each await, so a story closed mid-open
   *  (Escape, or the About panel taking over) can never finish opening behind the user. */
  private generation = 0;
  /** Pending auto-return once the narration ends — cancelled if the user closes first. */
  private autoCloseTimer: number | null = null;

  constructor(private readonly deps: StoryModeDeps) {
    const g = settings.storyMode.grade;
    this.grade = document.createElement('div');
    this.grade.id = 'story-grade';
    this.grade.style.setProperty('--grade-color', g.overlayColor);
    this.grade.style.setProperty('--grade-opacity', String(g.overlayOpacity));
    this.grade.style.setProperty('--grade-vignette', String(g.vignette));
    document.body.appendChild(this.grade);

    // CLOSE control. The ring cursor is gone (2026-08-18 v5), so the story needs a real, visible
    // way out — same quiet uppercase + underline-sweep language as KNOW MORE, so leaving reads as
    // the counterpart of entering rather than as a different piece of chrome.
    this.closeBtn = document.createElement('button');
    this.closeBtn.id = 'story-close';
    this.closeBtn.type = 'button';
    this.closeBtn.textContent = settings.storyMode.closeLabel;
    this.closeBtn.addEventListener('click', () => {
      this.deps.onCloseSound?.();
      void this.exit();
    });
    document.body.appendChild(this.closeBtn);
    window.addEventListener('keydown', this.onKeyDown);

    this.narrator.onSpeakingChange = (speaking) => {
      // The pad steps back under each sentence and returns in the gaps — the bed never
      // disappears, it just makes room for her.
      this.ambience.setDucked(speaking);
      this.deps.onSpeakingChange?.(speaking);
    };
    // AUTO-RETURN (2026-08-18 v6, user: "after the voice thing is done, take off that shader mode
    // and come back to normal"). The last line is held long enough to read, then the smoke pulls
    // back by itself. CLOSE and Escape still work throughout, so nobody is ever held here.
    this.narrator.onFinished = () => {
      this.log.info('narration finished — returning to the scene.');
      this.autoCloseTimer = window.setTimeout(
        () => void this.exit(),
        settings.storyMode.autoCloseAfterSec * 1000,
      );
    };
  }

  /** Passthrough for the debug panel — see {@link StoryNarrator.activeVoiceName}. */
  /** Escape always leaves — a full-screen takeover must have a keyboard way out. */
  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.open) {
      this.deps.onCloseSound?.();
      void this.exit();
    }
  };

  get activeVoiceName(): string {
    return this.narrator.activeVoiceName;
  }

  get isOpen(): boolean {
    return this.open;
  }

  /** Does this chapter have a story behind the hold? */
  static hasStory(chapterIndex: number): boolean {
    return (
      settings.storyMode.enabled &&
      settings.storyMode.chapters.includes(chapterIndex) &&
      !!settings.storyMode.stories[chapterIndex]
    );
  }

  /** Track which chapter the viewer is in, so a story opened without an explicit index knows
   *  which one to tell. */
  setChapter(index: number): void {
    this.currentChapter = index;
  }

  /** Open the current chapter's story. */
  private async enter(): Promise<void> {
    if (this.open || this.busy) return;
    const index = this.currentChapter;
    const story = settings.storyMode.stories[index];
    if (!StoryMode.hasStory(index) || !story) return;

    this.busy = true;
    const gen = this.generation;
    this.log.info(`story open — chapter ${index + 1}.`);
    this.deps.onOpen?.(index);

    this.open = true;
    this.closeBtn.classList.add('is-shown');
    // The room comes up with the smoke, well before she speaks, so the space is established
    // first and her voice arrives INTO it.
    this.ambience.start();
    // STORY MODE OWNS THE SCREEN. Kept here rather than in the caller's `onOpen`: hiding the
    // chapter UI is intrinsic to what a story IS, so a second entry point could never forget it.
    document.documentElement.classList.add('is-story');
    // No break, no flash, no shake. The smoke simply keeps flowing from wherever the hold left
    // it until the scene is almost entirely gone — one continuous movement from the first
    // moment of the press to the last of the grade.
    this.applyGrade(true);

    // Let the smoke take the scene before any words arrive.
    await wait(settings.storyMode.subtitleDelaySec * 1000);
    if (gen !== this.generation || !this.open) {
      this.busy = false;
      return;
    }
    this.narrator.start(story.title, story.paragraphs);
    this.busy = false;
  }

  /** Close the story and put every material back exactly as it was. */
  private async exit(): Promise<void> {
    // `busy` alone is enough to matter here: a close arriving mid-open must still invalidate the
    // open sequence, even though `open` is not true yet.
    if (!this.open && !this.busy) return;
    this.generation += 1;
    this.busy = false;
    if (this.autoCloseTimer !== null) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
    this.ambience.stop();
    const wasOpen = this.open;
    this.open = false;
    if (!wasOpen) {
      // Cancelled before it ever showed — undo the world changes `onOpen` made and stop.
      this.applyGrade(false);
      this.closeBtn.classList.remove('is-shown');
      document.documentElement.classList.remove('is-story');
      this.deps.onClose?.();
      return;
    }
    this.log.info('story closed — restoring materials.');
    this.narrator.stop();
    this.applyGrade(false);
    this.closeBtn.classList.remove('is-shown');
    document.documentElement.classList.remove('is-story');
    this.deps.onClose?.();
  }

  /**
   * Open a chapter's story directly, skipping the hold.
   *
   * The touch/keyboard path: there is no press-and-hold gesture without a hover-capable pointer,
   * so the "Know More" button's click has to be the way in there. `Experience` gates this on
   * `(hover: hover)` so desktop still gets the ritual the user designed.
   */
  openChapter(index: number): void {
    this.currentChapter = index;
    void this.enter();
  }

  /** Public close, for Escape / a chapter change / teardown. */
  forceClose(): void {
    void this.exit();
  }

  /**
   * Ease the cinematic dark grade in or out.
   *
   * Targets are collected FRESH on every open: the material set is not fixed at boot (the
   * `CustomScene1` toggle swaps scene-1's materials wholesale, and quality tiers swap the grass
   * material), so a list captured once would tint materials nothing is wearing any more.
   */
  private applyGrade(on: boolean): void {
    const cfg = settings.storyMode.grade;
    this.gradeTween?.kill();
    this.grade.classList.toggle('is-shown', on);
    this.gradeTween = gsap.to(this.gradeAmount, {
      value: on ? 1 : 0,
      duration: on ? cfg.durationSec : cfg.outDurationSec,
      // MAGNETIC (user: "I want like magnetic pulling and off like that"). `expo.inOut` barely
      // moves at first, rushes through the middle, then settles long — the same shape as the
      // scene's existing magnet glides, so entering a story feels like the camera pulls it in.
      ease: on ? 'expo.inOut' : 'power3.inOut',
      onUpdate: () => {
        // SNAP THE TAIL. An easing curve approaches its endpoint asymptotically, so on the way
        // out the last stretch is spent at values like 4e-4 — invisible, but enough to keep the
        // shader's grade branch alive and the sky a hair off its authored colour until the
        // tween's final frame lands. Below this threshold there is nothing left to see, so
        // finish the job early rather than depending on `onComplete` arriving promptly (a
        // throttled tab or one heavy frame can delay it indefinitely).
        const v = this.gradeAmount.value;
        const applied = !on && v < 0.002 ? 0 : v;
        this.deps.grade.amount.value = applied;
        this.deps.setAtmosphere(applied);
      },
      onComplete: () => {
        // Land exactly on the endpoints — at 0 the shader branch is skipped entirely and the sky
        // is bit-identical to the authored one.
        this.deps.grade.amount.value = on ? 1 : 0;
        this.deps.setAtmosphere(on ? 1 : 0);
      },
    });
  }

  /** Per-frame: keep the cloud field drifting whenever any grade is on screen, including while
   *  it eases back out — the smoke is always moving, never a static tint. */
  update(delta: number): void {
    if (this.gradeAmount.value <= 0) return;
    this.deps.grade.update(delta);
    // SAFETY NET for a stranded fade-out. GSAP drives the grade from rAF, which a browser stops
    // servicing in a background tab — so a story closed just before the tab was hidden can leave
    // the tween parked a hair above zero, with the grade branch alive and the sky fractionally
    // off its authored colour for as long as the page stays open. This runs on the app's own
    // tick, so whenever frames ARE being produced the tail is finished off regardless of what
    // the tween did.
    if (!this.open && this.gradeAmount.value < 0.002) {
      this.gradeTween?.kill();
      this.gradeAmount.value = 0;
      this.deps.grade.amount.value = 0;
      this.deps.setAtmosphere(0);
    }
  }

  dispose(): void {
    this.gradeTween?.kill();
    this.deps.grade.amount.value = 0;
    this.deps.setAtmosphere(0);
    window.removeEventListener('keydown', this.onKeyDown);
    document.documentElement.classList.remove('is-story');
    this.narrator.dispose();
    this.ambience.dispose();
    this.closeBtn.remove();
    this.grade.remove();
  }
}
